import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useZip } from "@/context/ZipContext";
import { Button } from "@/components/ui/button";
import { LogOut, MapPin } from "lucide-react";
import { useState } from "react";
import BrandBanner from "@/components/BrandBanner";

export default function Navbar() {
  const { user, startGoogleLogin, signOut, openLogin } = useAuth();
  const { zip, setZip } = useZip();
  const [zipInput, setZipInput] = useState(zip);
  const [zipOpen, setZipOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const saveZip = () => {
    const clean = (zipInput || "").replace(/\D/g, "").slice(0, 5);
    setZip(clean);
    setZipOpen(false);
  };

  const links = [
    { to: "/", label: "FEED" },
    { to: "/best-deals", label: "BEST" },
    { to: "/stores", label: "STORES" },
    { to: "/dashboard", label: "SAVED" },
    { to: "/submit", label: "SUBMIT" },
    { to: "/sponsor", label: "PROMOTE" },
    ...(user?.is_admin ? [{ to: "/admin", label: "ADMIN" }] : []),
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b-2 border-black bg-[#fff8ef] relative"
      data-testid="site-navbar"
      style={{ isolation: "isolate", contain: "layout paint" }}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <BrandBanner to="/" />

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={`px-3 py-2 text-xs tracking-[0.18em] border border-transparent hover:border-black ${
                  active ? "bg-black text-[#f6f4f0]" : ""
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setZipOpen((v) => !v)}
            data-testid="zip-btn"
            className="hidden sm:flex items-center gap-1.5 px-3 h-9 border-2 border-[#2d2a32] rounded-full text-[11px] font-bold tracking-[0.15em] bg-white hover:bg-[#f7c873]"
          >
            <MapPin className="w-3.5 h-3.5" />
            {zip ? zip : "SET ZIP"}
          </button>
          {zipOpen && (
            <div
              className="absolute top-full right-6 mt-2 z-50 border-2 border-[#2d2a32] rounded-xl p-4 bg-white hard-shadow w-64"
              data-testid="zip-popover"
            >
              <div className="text-[10px] tracking-[0.25em] text-[#5c5c5c] mb-2 font-bold">
                YOUR ZIP CODE
              </div>
              <input
                autoFocus
                inputMode="numeric"
                maxLength={5}
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && saveZip()}
                placeholder="e.g. 78701"
                data-testid="zip-input"
                className="w-full h-11 border-2 border-[#2d2a32] rounded-md px-3 mono text-base focus:outline-none focus:border-[#ff6b6b]"
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={saveZip}
                  data-testid="zip-save"
                  className="flex-1 h-9 border-2 border-[#2d2a32] rounded-full bg-[#ff6b6b] text-white text-[11px] tracking-[0.2em] font-bold hover:bg-[#2d2a32]"
                >
                  SAVE
                </button>
                {zip && (
                  <button
                    onClick={() => { setZip(""); setZipInput(""); setZipOpen(false); }}
                    data-testid="zip-clear"
                    className="h-9 px-3 border-2 border-[#2d2a32] rounded-full bg-white text-[11px] tracking-[0.2em] font-bold hover:bg-[#2d2a32] hover:text-white"
                  >
                    CLEAR
                  </button>
                )}
              </div>
              <p className="text-[10px] text-[#5c5c5c] mt-2 leading-relaxed">
                We'll prioritise deals from stores near you.
              </p>
            </div>
          )}
          {user ? (
            <>
              <div
                className="hidden sm:flex items-center gap-2 text-xs"
                data-testid="user-badge"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 border border-black"
                  />
                ) : (
                  <div className="w-7 h-7 border border-black bg-white flex items-center justify-center text-[10px]">
                    {user.name?.[0] || "U"}
                  </div>
                )}
                <span className="tracking-wider">{user.name}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await signOut();
                  navigate("/");
                }}
                data-testid="logout-btn"
                className="rounded-none border border-black hover:bg-black hover:text-[#f6f4f0]"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => openLogin("signup")}
                data-testid="signup-nav-btn"
                className="rounded-full bg-[#ff6b6b] hover:bg-[#e85555] text-white tracking-[0.18em] text-xs px-5 hard-shadow-sm"
              >
                SIGN UP
              </Button>
              <Button
                onClick={() => openLogin("signin")}
                data-testid="login-google-btn"
                className="rounded-full bg-[#2d2a32] hover:bg-[#ff6b6b] text-white tracking-[0.18em] text-xs px-5 hard-shadow-sm"
              >
                SIGN IN
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      <div className="md:hidden border-t border-black flex overflow-x-auto">
        {links.map((l) => {
          const active = location.pathname === l.to;
          return (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`mnav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 text-[11px] tracking-[0.18em] border-r border-black whitespace-nowrap ${
                active ? "bg-black text-[#f6f4f0]" : ""
              }`}
            >
              {l.label}
            </Link>
          );
        })}
      </div>
    </header>
  );
}
